import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Video, 
  Mic, 
  FileText, 
  MessageSquare,
  Twitter,
  Instagram,
  Youtube,
  Linkedin,
  Facebook,
  Users,
  BarChart2,
  DollarSign,
  Calendar,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { QuizAnswers, ContentType, Platform, Goal, Frequency, QuizStep } from '../types/quiz';
import { sendToAIScenario, saveQuizResults } from '../lib/quiz';
import { useSupabaseAuth } from '../lib/hooks/useSupabase';

const OnboardingQuiz = () => {
  const navigate = useNavigate();
  const { supabaseClient } = useSupabaseAuth(); // Get Supabase client from hook
  const [currentStep, setCurrentStep] = useState<QuizStep>('content_types');
  const [answers, setAnswers] = useState<QuizAnswers>({
    contentTypes: [],
    platforms: [],
    mainGoal: null,
    publishFrequency: null,
  });
  const [aiTip, setAiTip] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quizCompleted, setQuizCompleted] = useState<boolean>(false);

  // Check if quiz is already completed - only on initial mount
  useEffect(() => {
    const isCompleted = localStorage.getItem('quizCompleted') === 'true';
    
    if (isCompleted) {
      navigate('/dashboard');
    }
  }, [navigate]); // Add navigate to dependencies

  // Handle content type selection (multiple choice)
  const handleContentTypeToggle = (type: ContentType) => {
    setAnswers(prev => {
      const isSelected = prev.contentTypes.includes(type);
      return {
        ...prev,
        contentTypes: isSelected
          ? prev.contentTypes.filter(t => t !== type)
          : [...prev.contentTypes, type]
      };
    });
  };

  // Handle platform selection (multiple choice)
  const handlePlatformToggle = (platform: Platform) => {
    setAnswers(prev => {
      const isSelected = prev.platforms.includes(platform);
      return {
        ...prev,
        platforms: isSelected
          ? prev.platforms.filter(p => p !== platform)
          : [...prev.platforms, platform]
      };
    });
  };

  // Handle single choice selections
  const handleSingleChoice = <T extends Goal | Frequency>(
    field: 'mainGoal' | 'publishFrequency',
    value: T
  ) => {
    setAnswers(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Check if current step is valid to proceed
  const isStepValid = () => {
    switch (currentStep) {
      case 'content_types':
        return answers.contentTypes.length > 0;
      case 'platforms':
        return answers.platforms.length > 0;
      case 'main_goal':
        return answers.mainGoal !== null;
      case 'publish_frequency':
        return answers.publishFrequency !== null;
      default:
        return true;
    }
  };

  // Move to next step
  const handleNextStep = () => {
    if (!isStepValid()) return;

    switch (currentStep) {
      case 'content_types':
        setCurrentStep('platforms');
        break;
      case 'platforms':
        setCurrentStep('main_goal');
        break;
      case 'main_goal':
        setCurrentStep('publish_frequency');
        break;
      case 'publish_frequency':
        handleSubmitQuiz();
        break;
      default:
        break;
    }
  };

  // Submit quiz and get AI tip
  const handleSubmitQuiz = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get current user using the supabaseClient from hook
      const { data: { user }, error: userError } = await supabaseClient.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Save quiz answers to user_preferences table
      const { error: preferencesError } = await supabaseClient
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          content_types: answers.contentTypes,
          platforms: answers.platforms,
          main_goal: answers.mainGoal,
          publish_frequency: answers.publishFrequency
        }, {
          onConflict: 'user_id'
        });

      if (preferencesError) throw preferencesError;

      // Get AI tip
      const tip = await sendToAIScenario(answers);
      setAiTip(tip);
      
      // Move to results step
      setCurrentStep('results');
      
      // Store quiz completion status and answers
      saveQuizResults(answers);
      localStorage.setItem('quizCompleted', 'true');
      
      // Move to results step
      console.log('Quiz completed with answers:', answers);
    } catch (err) {
      setError('Failed to process your answers. Please try again.');
      console.error('Error submitting quiz:', err);
    } finally {
      setLoading(false);
    }
  };

  // Complete onboarding and go to dashboard
  const handleCompletionAndRedirect = () => {
    setQuizCompleted(true);
    navigate('/dashboard');
  };

  // Render content type selection step
  const renderContentTypesStep = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Which type of content do you primarily create?
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Select all that apply to your content creation strategy.
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleContentTypeToggle('video')}
          className={`flex items-center p-4 rounded-lg border-2 transition-all ${
            answers.contentTypes.includes('video')
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
              : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
          }`}
        >
          <div className={`p-2 rounded-full mr-3 ${
            answers.contentTypes.includes('video')
              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
              : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
          }`}>
            <Video className="h-5 w-5" />
          </div>
          <span className="text-left font-medium text-gray-900 dark:text-white">Video Content</span>
          {answers.contentTypes.includes('video') && (
            <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          )}
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleContentTypeToggle('podcast')}
          className={`flex items-center p-4 rounded-lg border-2 transition-all ${
            answers.contentTypes.includes('podcast')
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
              : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
          }`}
        >
          <div className={`p-2 rounded-full mr-3 ${
            answers.contentTypes.includes('podcast')
              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
              : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
          }`}>
            <Mic className="h-5 w-5" />
          </div>
          <span className="text-left font-medium text-gray-900 dark:text-white">Podcast</span>
          {answers.contentTypes.includes('podcast') && (
            <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          )}
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleContentTypeToggle('blog')}
          className={`flex items-center p-4 rounded-lg border-2 transition-all ${
            answers.contentTypes.includes('blog')
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
              : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
          }`}
        >
          <div className={`p-2 rounded-full mr-3 ${
            answers.contentTypes.includes('blog')
              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
              : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
          }`}>
            <FileText className="h-5 w-5" />
          </div>
          <span className="text-left font-medium text-gray-900 dark:text-white">Blog Articles</span>
          {answers.contentTypes.includes('blog') && (
            <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          )}
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleContentTypeToggle('social')}
          className={`flex items-center p-4 rounded-lg border-2 transition-all ${
            answers.contentTypes.includes('social')
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
              : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
          }`}
        >
          <div className={`p-2 rounded-full mr-3 ${
            answers.contentTypes.includes('social')
              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
              : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
          }`}>
            <MessageSquare className="h-5 w-5" />
          </div>
          <span className="text-left font-medium text-gray-900 dark:text-white">Social Media Posts</span>
          {answers.contentTypes.includes('social') && (
            <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          )}
        </motion.button>
      </div>
    </div>
  );

  // Render platform selection step
  const renderPlatformStep = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Which platforms do you use?
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Select all platforms where you publish your content.
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { id: 'twitter', label: 'Twitter (X)', icon: Twitter, color: '#1DA1F2' },
          { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
          { id: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000' },
          { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
          { id: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' }
        ].map(platform => {
          const Icon = platform.icon;
          const isSelected = answers.platforms.includes(platform.id as Platform);
          
          return (
            <motion.button
              key={platform.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handlePlatformToggle(platform.id as Platform)}
              className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              <div className="p-2 rounded-full mr-3 bg-white dark:bg-dark-700">
                <Icon className="h-5 w-5" style={{ color: platform.color }} />
              </div>
              <span className="text-left font-medium text-gray-900 dark:text-white">{platform.label}</span>
              {isSelected && (
                <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );

  // Render goal selection step
  const renderGoalStep = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        What is your main goal as a creator?
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Select the primary objective for your content strategy.
      </p>
      
      <div className="space-y-3">
        {[
          { id: 'grow_audience', label: 'Grow Audience', icon: Users, description: 'Increase followers and subscribers' },
          { id: 'increase_engagement', label: 'Increase Engagement', icon: BarChart2, description: 'Get more likes, comments, and shares' },
          { id: 'monetize', label: 'Monetize Content', icon: DollarSign, description: 'Generate revenue from your content' },
          { id: 'build_brand', label: 'Build Brand', icon: Sparkles, description: 'Establish authority and recognition' },
        ].map(goal => {
          const Icon = goal.icon;
          const isSelected = answers.mainGoal === goal.id;
          
          return (
            <motion.button
              key={goal.id}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => handleSingleChoice('mainGoal', goal.id as Goal)}
              className={`w-full flex items-center p-4 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              <div className={`p-2 rounded-full mr-3 ${
                isSelected
                  ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
              }`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="text-left">
                <span className="font-medium text-gray-900 dark:text-white">{goal.label}</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">{goal.description}</p>
              </div>
              {isSelected && (
                <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );

  // Render frequency selection step
  const renderFrequencyStep = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        How often do you plan to publish content?
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Select your ideal publishing frequency.
      </p>
      
      <div className="space-y-3">
        {[
          { id: 'daily', label: 'Daily', description: 'Publish new content every day' },
          { id: 'weekly', label: 'Weekly', description: 'Publish new content once a week' },
          { id: 'monthly', label: 'Monthly', description: 'Publish new content once a month' },
          { id: 'on_demand', label: 'On Demand', description: 'Publish content when ready, no fixed schedule' },
        ].map(frequency => {
          const isSelected = answers.publishFrequency === frequency.id;
          
          return (
            <motion.button
              key={frequency.id}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => handleSingleChoice('publishFrequency', frequency.id as Frequency)}
              className={`w-full flex items-center p-4 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              <div className={`p-2 rounded-full mr-3 ${
                isSelected
                  ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
              }`}>
                <Calendar className="h-5 w-5" />
              </div>
              <div className="text-left">
                <span className="font-medium text-gray-900 dark:text-white">{frequency.label}</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">{frequency.description}</p>
              </div>
              {isSelected && (
                <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );

  // Render results step
  const renderResultsStep = () => (
    <div className="space-y-6 text-center">
      <div className="mx-auto w-16 h-16 bg-indigo-100 dark:bg-indigo-900/40 rounded-full flex items-center justify-center">
        <Sparkles className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        Your Content Strategy
      </h2>
      
      <div className="bg-white dark:bg-dark-800 p-6 rounded-lg border border-gray-200 dark:border-dark-700 shadow-sm">
        <p className="text-gray-800 dark:text-gray-200 text-lg">
          {aiTip}
        </p>
      </div>
      
      <div className="pt-4">
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          We've customized your dashboard based on your preferences. You can always adjust your settings later.
        </p>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCompletionAndRedirect}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center mx-auto"
        >
          <span>Continue to Dashboard</span>
          <ArrowRight className="ml-2 h-5 w-5" />
        </motion.button>
      </div>
    </div>
  );

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'content_types':
        return renderContentTypesStep();
      case 'platforms':
        return renderPlatformStep();
      case 'main_goal':
        return renderGoalStep();
      case 'publish_frequency':
        return renderFrequencyStep();
      case 'results':
        return renderResultsStep();
      default:
        return null;
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    const steps = ['content_types', 'platforms', 'main_goal', 'publish_frequency', 'results'];
    const currentIndex = steps.indexOf(currentStep);
    return (currentIndex / (steps.length - 1)) * 100;
  };

  // If quiz is already completed, redirect to dashboard
  if (quizCompleted) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-dark-800 rounded-xl shadow-lg max-w-2xl w-full p-8"
        >
          {/* Progress bar */}
          {currentStep !== 'results' && (
            <div className="mb-8">
              <div className="h-2 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${getProgressPercentage()}%` }}
                  className="h-full bg-indigo-600"
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span>Start</span>
                <span>Complete</span>
              </div>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
            </div>
          )}
          
          {/* Step content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
          
          {/* Navigation buttons */}
          {currentStep !== 'results' && (
            <div className="mt-8 flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextStep}
                disabled={!isStepValid() || loading}
                className={`px-6 py-3 rounded-lg flex items-center transition-colors ${
                  isStepValid() && !loading
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    {currentStep === 'publish_frequency' ? 'Submit' : 'Next'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </motion.button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingQuiz;